import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Zap,
  Landmark,
  Globe2,
  BadgeDollarSign,
  CalendarRange,
  ShieldCheck,
} from 'lucide-react';
import HeroSection from '../components/HeroSection';
import ceoImg from '../assets/team/Alan_Karul_CEO.jpeg';
import headOfOpsImg from '../assets/team/Raymond_Mizrachi.jpeg';
import cfoImg from '../assets/team/Matthew.jpeg';
import legalCounselImg from '../assets/team/Brian_Schechter_legal_counsel.jpeg';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

const leadershipProfiles = [
  {
    name: 'Alan Karul',
    role: 'CEO',
    quote:
      'We focus on long-term partnerships and reliable access to capital.',
    img: ceoImg,
  },
  {
    name: 'Raymond Mizrachi',
    role: 'Chief of Operations',
    quote:
      'We operate with speed, communication, and a focus on closing deals.',
    img: headOfOpsImg,
  },
  {
    name: 'Matthew Brennen',
    role: 'Chief Financial Officer',
    quote:
      'We bring financial discipline and clarity to every funding decision.',
    img: cfoImg,
  },
  {
    name: 'Brian Schechter',
    role: 'Chief Legal Counsel',
    quote:
      'Clear structure and disciplined processes support every deal we fund.',
    img: legalCounselImg,
  },
];

const fundedBusinesses = [
  {
    src: '/images/offline/busy-restaurant.jpg',
    alt: 'Busy restaurant with many customers',
    title: 'Fast Approvals',
    subtitle: 'Decisions in hours, not days.',
    icon: Zap,
  },
  {
    src: '/images/offline/construction-contractor-signing.jpg',
    alt: 'Construction contractor signing papers with a client on a work site',
    title: 'Funding up to $1M',
    subtitle: 'Flexible capital designed to scale with your business.',
    icon: Landmark,
  },
  {
    src: '/images/offline/photo-1629909613654-28e377c37b09.jpg',
    alt: 'Dental clinic business funding',
    title: 'Fast funding built for growing businesses.',
    subtitle: 'Real approvals. Flexible funding. Same-day capital available.',
    icon: Globe2,
  },
];

const productOverviewSlides = [
  {
    src: '/images/offline/fast-approvals-owner.jpg',
    alt: 'Small business owner reviewing a fast funding update',
    title: 'Fast Approvals',
    subtitle: 'Quick underwriting decisions designed to keep your deals moving.',
  },
  {
    src: '/images/offline/photo-1556740738-b6a63e27c4df.jpg',
    alt: 'Busy clothing store with shoppers',
    title: 'Funding up to $1M',
    subtitle: 'Flexible capital solutions for growing businesses.',
  },
  {
    src: '/images/offline/dental-consultation-modern.jpg',
    alt: 'Modern dental consultation in a U.S. clinic',
    title: 'Nationwide Coverage',
    subtitle: 'Serving businesses across the United States.',
  },
];

const fundingHighlightCards = [
  {
    label: 'Funding Amount',
    value: 'Up to $1M Available funding',
    icon: BadgeDollarSign,
    slides: [
      {
        src: '/images/offline/photo-1551836022-4c4c79ecde51.jpg',
        alt: 'Business consultation about funding opportunities',
      },
      {
        src: '/images/offline/photo-1556742049-0cfed4f6a45d.jpg',
        alt: 'Restaurant owner with funding support',
      },
      {
        src: '/images/offline/photo-1521737604893-d14cc237f11d.jpg',
        alt: 'Growing business team with capital',
      },
    ],
  },
  {
    label: 'PAYBACK STRUCTURE',
    value: 'Payback period up to 24 months',
    icon: CalendarRange,
    slides: [
      {
        src: '/images/offline/photo-1504384308090-c894fdcc538d.jpg',
        alt: 'Construction business planning project terms',
      },
      {
        src: '/images/offline/photo-1554224155-6726b3ff858f.jpg',
        alt: 'Business owner organizing paperwork',
      },
      {
        src: '/images/offline/photo-1515378960530-7c0da6231fb1.jpg',
        alt: 'Small business owner reviewing application details',
      },
    ],
  },
  {
    label: 'Experience',
    value: 'Fast Approvals in hours',
    icon: ShieldCheck,
    slides: [
      {
        src: '/images/offline/photo-1576091160550-2173dba999ef.jpg',
        alt: 'Experience in diverse industries',
      },
      {
        src: '/images/offline/photo-1557804506-669a67965ba0.jpg',
        alt: 'Experienced team collaborating on growth plans',
      },
      {
        src: '/images/offline/photo-1528605248644-14dd04022da1.jpg',
        alt: 'Successful business partnership meeting',
      },
    ],
  },
];

export default function Home() {
  const [productSlide, setProductSlide] = useState(0);
  const [highlightSlides, setHighlightSlides] = useState([0, 0, 0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setProductSlide((prev) => (prev + 1) % productOverviewSlides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setHighlightSlides((prev) =>
        prev.map((value, idx) => (value + 1) % fundingHighlightCards[idx].slides.length)
      );
    }, 4200);

    return () => clearInterval(interval);
  }, []);

  const activeProductSlide =
    productOverviewSlides[productSlide] || productOverviewSlides[0];

  return (
    <main className="bg-white text-gray-900">
      <HeroSection
        title={
          '<span class="text-amber-400">Fast</span> <span class="text-white/95">capital.</span><br/><span class="text-white">Flexible funding. Built for business.</span>'
        }
        subtitle="Fast approvals. Flexible funding. Trusted by businesses and brokers nationwide."
        image="/images/offline/photo-1486406146926-c627a92ad1ab.jpg"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link to="/partner" className="cta-secondary">
              Partner with Us
            </Link>

            <Link to="/apply" className="cta-primary">
              Apply for Funding
            </Link>
          </div>

          <p className="text-center text-sm font-medium tracking-[0.18em] uppercase text-white/70">
            Fast funding for businesses. A partner brokers can rely on.
          </p>
        </div>
      </HeroSection>

      <section className="border-b border-gray-100 bg-white pb-12 pt-5 md:pb-16 md:pt-8 lg:pb-20">
        <div className="page-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="section-intro-wide mx-auto max-w-3xl text-center"
          >
            <h2 className="text-3xl font-semibold leading-tight tracking-tight text-gray-900 md:text-5xl">
              Why Choose Bizfund
            </h2>

            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-gray-600 md:text-lg">
              Fast decisions, reliable approvals, and a team built for deal flow.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6"
          >
            {fundedBusinesses.map((business) => {
              const Icon = business.icon;

              return (
                <motion.div
                  key={business.title}
                  variants={fadeUp}
                  className="group relative h-[340px] overflow-hidden rounded-[28px] border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.18)] md:h-[430px]"
                >
                  <img
                    src={business.src}
                    alt={business.alt}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10" />

                  <div className="absolute inset-x-0 bottom-0 p-5 text-left sm:p-6 md:p-7">
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/10">
                      <Icon size={22} className="text-white" />
                    </div>

                    <h3 className="text-2xl font-semibold leading-tight text-white md:text-3xl">
                      {business.title}
                    </h3>

                    <p className="mt-3 text-sm leading-relaxed text-white/85 md:text-base">
                      {business.subtitle}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="page-section border-b border-gray-100 bg-[#F8FAFC]">
        <div className="page-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="section-intro-wide mx-auto max-w-3xl text-center"
          >
            <h2 className="mb-5 text-3xl font-semibold tracking-tight text-gray-900 md:mb-6 md:text-5xl">
              Direct funding solutions with real underwriting, fast turnaround times, and a team built to support your growth.
            </h2>

            <p className="text-base leading-relaxed text-gray-600 md:text-lg">
              Fast, flexible capital designed to support your business at every stage.
            </p>
          </motion.div>

          <div className="relative mx-auto mb-8 h-[280px] max-w-5xl overflow-hidden rounded-[30px] border border-black/5 shadow-[0_20px_60px_rgba(0,0,0,0.18)] sm:mb-10 sm:h-[380px] md:h-[500px]">
            <AnimatePresence mode="wait">
              <motion.img
                key={productSlide}
                src={activeProductSlide.src}
                alt={activeProductSlide.alt}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>

            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/5" />

            <div className="absolute bottom-0 max-w-xl p-5 sm:p-6 md:p-10">
              <motion.div
                key={`product-text-${productSlide}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-3xl font-semibold text-white md:text-5xl">
                  {activeProductSlide.title}
                </h3>

                <p className="mt-3 text-sm text-white/85 md:text-lg">
                  {activeProductSlide.subtitle}
                </p>
              </motion.div>
            </div>

            <div className="absolute bottom-5 right-5 flex gap-2 sm:bottom-6 sm:right-6">
              {productOverviewSlides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setProductSlide(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === productSlide ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/70'
                  }`}
                  aria-label={`Show product slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-4 text-3xl font-semibold tracking-tight text-gray-900 md:mb-5 md:text-5xl">
              Funding available up to $1M with terms up to 24 months.
            </h2>

            <p className="text-base leading-relaxed text-gray-600 md:text-lg">
              Apply directly or work with one of our approved broker partners.
            </p>

            <div className="mt-8 md:mt-10">
              <Link to="/what-we-offer" className="cta-primary">
                Apply for Funding
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="page-section border-b border-gray-100 bg-white">
        <div className="page-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="section-intro-wide text-center"
          >
            <h2 className="text-3xl font-semibold tracking-tight text-gray-900 md:text-5xl">
              Funding at a glance
            </h2>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6"
          >
            {fundingHighlightCards.map((card, cardIndex) => {
              const activeSlide =
                card.slides[highlightSlides[cardIndex]] || card.slides[0];
              const Icon = card.icon;

              return (
                <motion.div
                  key={card.label}
                  variants={fadeUp}
                  className="relative h-[300px] overflow-hidden rounded-[28px] border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.18)] md:h-[390px]"
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={`${card.label}-${highlightSlides[cardIndex]}`}
                      src={activeSlide.src}
                      alt={activeSlide.alt}
                      initial={{ opacity: 0, scale: 1.03 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.75 }}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </AnimatePresence>

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/5" />

                  <div className="absolute inset-x-0 bottom-0 p-5 text-left sm:p-6 md:p-7">
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/10">
                      <Icon size={22} className="text-white" />
                    </div>

                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-white/75">
                      {card.label}
                    </p>

                    <h3 className="text-2xl font-semibold leading-tight text-white md:text-3xl">
                      {card.value}
                    </h3>
                  </div>

                  <div className="absolute right-5 top-5 flex gap-1.5">
                    {card.slides.map((_, idx) => (
                      <button
                        key={`${card.label}-dot-${idx}`}
                        type="button"
                        onClick={() =>
                          setHighlightSlides((prev) =>
                            prev.map((value, i) => (i === cardIndex ? idx : value))
                          )
                        }
                        className={`h-2 rounded-full transition-all ${
                          idx === highlightSlides[cardIndex]
                            ? 'w-6 bg-white'
                            : 'w-2 bg-white/40 hover:bg-white/70'
                        }`}
                        aria-label={`Show ${card.label} slide ${idx + 1}`}
                      />
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

        </div>
      </section>

      <section className="page-section border-b border-gray-100 bg-[#FCFCFD] pb-16 md:pb-20 lg:pb-24">
        <div className="page-container">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="section-intro-wide mx-auto max-w-3xl text-center"
          >
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 md:text-5xl">
              Leadership you can trust
            </h2>

            <p className="mt-5 text-base leading-relaxed text-gray-600 md:text-lg">
              Experienced leadership focused on fast decisions, strong partnerships, and consistent execution.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid gap-6 md:grid-cols-2 md:gap-8"
          >
            {leadershipProfiles.map((person) => (
              <motion.div
                key={person.name}
                variants={fadeUp}
                className="flex flex-col rounded-[28px] border border-gray-100 bg-white p-6 text-center shadow-[0_10px_35px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_45px_rgba(15,23,42,0.10)] md:p-8"
              >
                <div className="mx-auto mb-5 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-gray-100 shadow-md ring-1 ring-gray-100 md:mb-6">
                  {person.name === 'Matthew Brennen' ? (
                    <div className="h-full w-full rounded-full bg-gray-100" />
                  ) : (
                    <img
                      src={person.img}
                      alt={person.name}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>

                <h3 className="text-xl font-semibold text-gray-900">{person.name}</h3>

<p className="mb-4 mt-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-600">
  {person.role}
</p>

                <blockquote className="flex-1 text-sm italic leading-relaxed text-gray-600 md:text-base">
                  {person.quote}
                </blockquote>
              </motion.div>
            ))}
          </motion.div>


        </div>
      </section>

    </main>
  );
}
